import React, { useState, useMemo } from 'react';
import { Box, Typography, 
  FormControl, MenuItem, InputLabel, Select,  
} from '@mui/material';
import { getSortableValue, formatXValue } from 'state/helpers';
import BarChart from './BarChart';


const SortBarChart = ({ 
  data = [], 
  title = "",
  bottomLegend, 
  leftLegend, 
  isShowAvgLine = true, 
  isSortVisible = true, 
  isSortDisabled, 
  isHorizontalBottomLegend, 
  digit = 2,
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
  itemWidthOffSet = 0,
  translateXOffSet = 0,
  translateYOffSet = 0,
  itemsSpacingOffSet = 0,
}) => {
  const [sortType, setSortType] = useState('xAxis');  

  const sortBarChartDataByXAxis = (chartData, xAxisName) => {
    if (!Array.isArray(chartData)) {
      throw new TypeError('Expected an array of chart data.');
    }
    
    return chartData
      .slice()
      .sort((a, b) => {
        const valueA = getSortableValue(a[xAxisName] || a['x']);
        const valueB = getSortableValue(b[xAxisName] || b['x']);
        
        if (typeof valueA === "string" && typeof valueB === "string") {
          return valueA.localeCompare(valueB);
        }

        return valueA - valueB;
      })
      .map((item) => ({
        ...item,
        [xAxisName]: formatXValue(item[xAxisName] || item['x']), 
      }));
  }

  const sortBarChartDataByYAxis = (chartData, ...axisNames) => {
    return chartData.sort((a, b) => {
      const sumA = axisNames.reduce((sum, axis) => sum + (typeof a[axis] === 'number' ? a[axis] : 0), 0);
      const sumB = axisNames.reduce((sum, axis) => sum + (typeof b[axis] === 'number' ? b[axis] : 0), 0);
      return sumA - sumB;
    }).map((item) => ({
      ...item,
      x: formatXValue(item.x), 
    }));
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }
    
    const sort = {
      yAxis: () => sortBarChartDataByYAxis(data, 'y'),
      xAxis: () => sortBarChartDataByXAxis(data, 'x'),
    };

    return sort[sortType]?.() || data;
  }, [sortType, data]);
  
  if (!sortedData || sortedData.length === 0 || !Array.isArray(sortedData)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }

  
  return (
    <Box sx={{ height: '100%', width: '100%' }}>       
      {isSortVisible && (
        <FormControl 
          size="small"
          sx={{ m: "0.5rem" }}
          disabled={isSortDisabled} 
        >
          <InputLabel>Sort</InputLabel>
          <Select
            value={sortType}
            label="Sort"
            onChange={(e) => setSortType(e.target.value)}            
          >
            <MenuItem value="xAxis">by xAxis</MenuItem>
            <MenuItem value="yAxis">by yAxis</MenuItem>                                            
          </Select>
        </FormControl>
      )} 

      <BarChart 
        data={sortedData} 
        title={title}
        bottomLegend={bottomLegend} 
        leftLegend={leftLegend} 
        isShowAvgLine={isShowAvgLine} 
        isHorizontalBottomLegend={isHorizontalBottomLegend}
        digit={digit}
        topOffSet={topOffSet}
        bottomOffSet={bottomOffSet}
        bottomLegendOffSet={bottomLegendOffSet} 
        itemWidthOffSet={itemWidthOffSet} 
        translateXOffSet={translateXOffSet}
        translateYOffSet={translateYOffSet}
        itemsSpacingOffSet={itemsSpacingOffSet}
      />
    </Box>
  );
};

export default SortBarChart;
